import clsx from 'clsx';
import type { HTMLProps } from 'react';

import { isInlineColorValue } from '@commons/helpers/styles';
import type { ColorAndTheme, InlineColor } from '@commons/types/styles';

import Icon, { type IconType } from '../Icon';
import Text from '../Text';
import styles from './index.module.scss';

// Omits the size property from the HTMLProps type to avoid conflicts.

type HTMLButtonProps = Omit<HTMLProps<HTMLButtonElement>, 'size'>;

// Type definition for the IconButton properties.

type Props = HTMLButtonProps & {
  type?: IconType;
  text?: string;
  size?: 'sm' | 'md' | 'lg';
  colors?: {
    icon?: ColorAndTheme | InlineColor;
    bg?: ColorAndTheme | InlineColor;
  };
  activeColors?: {
    icon?: ColorAndTheme | InlineColor;
    bg?: ColorAndTheme | InlineColor;
  };
  active?: boolean;
};

/**
 * IconButton component.
 */

const IconButton = (props: Props) => {
  const {
    text,
    type,
    size = 'lg',
    colors,
    activeColors,
    active,
    disabled,
    className,
    style,
    ref,
    ...rest
  } = props;

  const classes = clsx(
    styles.iconButton,
    styles[`size-${size}`],
    active && styles.active,
    active && 'active',
    disabled && 'disabled',
    className
  );

  // Many combinations of colors are possible, so we use CSS variables to make it easier to style the component.
  const inlineStyles = {
    '--icon-btn-color': getIconButtonColorValue(colors?.icon),
    '--icon-btn-bg-color': getIconButtonColorValue(colors?.bg),
    '--icon-btn-active-color': getIconButtonColorValue(activeColors?.icon),
    '--icon-btn-active-bg-color': getIconButtonColorValue(activeColors?.bg),
    ...style,
  };

  let content = null;

  if (text) {
    content = (
      <Text as="span" marginBottom="none" className={styles.text}>
        {text}
      </Text>
    );
  }

  if (type) {
    content = <Icon type={type} className={styles.icon} />;
  }

  return (
    <button ref={ref} disabled={disabled} className={classes} style={inlineStyles} {...rest}>
      {content}
    </button>
  );
};

/**
 * Helper function to get the color value for inline styles.
 *
 * It can be either a CSS variable (for predefined colors) or an inline color value.
 *
 * @param color - A color property from the IconButton props.
 *
 * @returns An inline style color value, CSS variable or undefined.
 */

const getIconButtonColorValue = (color?: ColorAndTheme | InlineColor) => {
  if (!color) return undefined;

  return isInlineColorValue(color) ? color : `var(--color-${color})`;
};

export default IconButton;
